import { createRouter, createWebHashHistory, RouteRecordRaw } from "vue-router";
import store from "@/store";
import { Mutations } from "@/store/enums/StoreEnums";
import AuthService from "@/core/services/AuthService";
import { useAuthStore } from "@/store/pinia/auth";
const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    redirect: "/projects",
    component: () => import("@/layout/Layout.vue"),
    children: [
      {
        path: "/dashboard",
        name: "dashboard",
        component: () => import("@/views/Dashboard.vue"),
      },
      {
        path: "/news",
        name: "news",
        component: () => import("@/views/NewsList.vue"),
      },
      {
        path: "/settings",
        name: "settings",
        component: () => import("@/views/SettingsForm.vue"),
      },
      {
        path: "/ksr",
        name: "ksr",
        component: () => import("@/views/KsrTable.vue"),
      },
      {
        path: "/normative-data",
        name: "normative-data",
        component: () => import("@/views/normative/NormativeData.vue"),
      },
      {
        path: "/admin/normative-base",
        name: "normative-base-management",
        meta: { permission: "manage normative base" },
        component: () => import("@/views/normative/NormativeManagement.vue"),
      },
      {
        path: "/nomenclatures",
        name: "nomenclatures",
        component: () => import("@/views/nomenclature/NomenclatureList.vue"),
      },
      {
        path: "/organizations",
        name: "organizations",
        component: () => import("@/views/organization/OrganizationLayout.vue"),
        children: [
          {
            path: "",
            name: "organization-list",
            component: () =>
              import("@/views/organization/OrganizationList.vue"),
          },
          {
            path: "create",
            name: "organization-create",
            component: () =>
              import("@/views/organization/OrganizationCreate.vue"),
          },
          {
            path: ":id",
            name: "organization-detail",
            component: () =>
              import("@/views/organization/OrganizationDetail.vue"),
          },
          {
            path: ":id/edit",
            name: "organization-edit",
            component: () =>
              import("@/views/organization/OrganizationEdit.vue"),
          },
          {
            path: ":id/employees/create",
            name: "employee-create",
            component: () => import("@/views/organization/EmployeeCreate.vue"),
          },
        ],
      },
      {
        path: "projects",
        name: "projects",
        component: () => import("@/views/project/ProjectLayout.vue"),
        children: [
          {
            path: "",
            name: "project-list",
            component: () => import("@/views/project/ProjectList.vue"),
          },
          {
            path: ":id",
            name: "project-detail",
            component: () => import("@/views/project/ProjectDetail.vue"),
          },
          {
            path: ":id/reports",
            name: "project-report-layout",
            component: () => import("@/views/project/ProjectLayout.vue"),
            children: [
              {
                path: "create",
                name: "project-report-create",
                component: () =>
                  import("@/views/project/report/ReportForm.vue"),
              },
              {
                path: ":reportId",
                name: "project-report-view",
                meta: { minify: true },
                component: () =>
                  import("@/views/project/report/ReportView.vue"),
              },
              {
                path: ":reportId/imports",
                name: "project-report-import-layout",
                component: () =>
                  import("@/views/project/report/ReportImportLayout.vue"),
                children: [
                  {
                    path: "excel",
                    name: "project-report-import-excel",
                    component: () =>
                      import("@/views/project/report/import/ExcelForm.vue"),
                  },
                  {
                    path: "xml",
                    name: "project-report-import-xml",
                    component: () =>
                      import("@/views/project/report/import/XmlForm.vue"),
                  },
                ],
              },
            ],
          },
          {
            path: "create",
            name: "project-create",
            component: () => import("@/views/project/ProjectCreate.vue"),
          },
        ],
      },
      {
        path: "/builder",
        name: "builder",
        component: () => import("@/views/Builder.vue"),
      },
      {
        path: "/crafted/pages/profile",
        name: "profile",
        component: () => import("@/components/page-layouts/Profile.vue"),
        children: [
          {
            path: "overview",
            name: "profile-overview",
            component: () =>
              import("@/views/crafted/pages/profile/Overview.vue"),
          },
          {
            path: "projects",
            name: "profile-projects",
            component: () =>
              import("@/views/crafted/pages/profile/Projects.vue"),
          },
          {
            path: "campaigns",
            name: "profile-campaigns",
            component: () =>
              import("@/views/crafted/pages/profile/Campaigns.vue"),
          },
          {
            path: "documents",
            name: "profile-documents",
            component: () =>
              import("@/views/crafted/pages/profile/Documents.vue"),
          },
          {
            path: "connections",
            name: "profile-connections",
            component: () =>
              import("@/views/crafted/pages/profile/Connections.vue"),
          },
          {
            path: "activity",
            name: "profile-activity",
            component: () =>
              import("@/views/crafted/pages/profile/Activity.vue"),
          },
        ],
      },
      {
        path: "/crafted/pages/wizards/horizontal",
        name: "horizontal-wizard",
        component: () => import("@/views/crafted/pages/wizards/Horizontal.vue"),
      },
      {
        path: "/crafted/pages/wizards/vertical",
        name: "vertical-wizard",
        component: () => import("@/views/crafted/pages/wizards/Vertical.vue"),
      },
      {
        path: "/crafted/account",
        name: "account",
        component: () => import("@/views/crafted/account/Account.vue"),
        children: [
          {
            path: "overview",
            name: "account-overview",
            component: () => import("@/views/crafted/account/Overview.vue"),
          },
          {
            path: "settings",
            name: "account-settings",
            component: () => import("@/views/crafted/account/Settings.vue"),
          },
        ],
      },
      {
        path: "/apps/customers/getting-started",
        name: "apps-customers-getting-started",
        component: () => import("@/views/apps/customers/GettingStarted.vue"),
      },
      {
        path: "/apps/customers/customers-listing",
        name: "apps-customers-listing",
        component: () => import("@/views/apps/customers/CustomersListing.vue"),
      },
      {
        path: "/apps/customers/customer-details",
        name: "apps-customers-details",
        component: () => import("@/views/apps/customers/CustomerDetails.vue"),
      },
      {
        path: "/apps/subscriptions/getting-started",
        name: "apps-subscriptions-getting-started",
        component: () =>
          import("@/views/apps/subscriptions/GettingStarted.vue"),
      },
      {
        path: "/apps/subscriptions/subscription-list",
        name: "apps-subscriptions-subscription-list",
        component: () =>
          import("@/views/apps/subscriptions/SubscriptionList.vue"),
      },
      {
        path: "/apps/subscriptions/add-subscription",
        name: "apps-subscriptions-add-subscription",
        component: () =>
          import("@/views/apps/subscriptions/AddSubscription.vue"),
      },
      {
        path: "/apps/subscriptions/view-subscription",
        name: "apps-subscriptions-view-subscription",
        component: () =>
          import("@/views/apps/subscriptions/ViewSubscription.vue"),
      },
      {
        path: "/apps/calendar",
        name: "apps-calendar",
        component: () => import("@/views/apps/Calendar.vue"),
      },
      {
        path: "/apps/chat/private-chat",
        name: "apps-private-chat",
        component: () => import("@/views/apps/chat/Chat.vue"),
      },
      {
        path: "/apps/chat/group-chat",
        name: "apps-group-chat",
        component: () => import("@/views/apps/chat/Chat.vue"),
      },
      {
        path: "/apps/chat/drawer-chat",
        name: "apps-drawer-chat",
        component: () => import("@/views/apps/chat/DrawerChat.vue"),
      },
      {
        path: "/crafted/modals/general/invite-friends",
        name: "modals-general-invite-friends",
        component: () =>
          import("@/views/crafted/modals/general/InviteFriends.vue"),
      },
      {
        path: "/crafted/modals/general/view-user",
        name: "modals-general-view-user",
        component: () => import("@/views/crafted/modals/general/ViewUsers.vue"),
      },
      {
        path: "/crafted/modals/general/upgrade-plan",
        name: "modals-general-upgrade-plan",
        component: () =>
          import("@/views/crafted/modals/general/UpgradePlan.vue"),
      },
      {
        path: "/crafted/modals/general/share-and-earn",
        name: "modals-general-share-and-earn",
        component: () =>
          import("@/views/crafted/modals/general/ShareAndEarn.vue"),
      },
      {
        path: "/crafted/modals/forms/new-target",
        name: "modals-forms-new-target",
        component: () => import("@/views/crafted/modals/forms/NewTarget.vue"),
      },
      {
        path: "/crafted/modals/forms/new-card",
        name: "modals-forms-new-card",
        component: () => import("@/views/crafted/modals/forms/NewCard.vue"),
      },
      {
        path: "/crafted/modals/forms/new-address",
        name: "modals-forms-new-address",
        component: () => import("@/views/crafted/modals/forms/NewAddress.vue"),
      },
      {
        path: "/crafted/modals/forms/create-api-key",
        name: "modals-forms-create-api-key",
        component: () =>
          import("@/views/crafted/modals/forms/CreateApiKey.vue"),
      },
      {
        path: "/crafted/modals/wizards/two-factor-auth",
        name: "modals-wizards-two-factor-auth",
        component: () =>
          import("@/views/crafted/modals/wizards/TwoFactorAuth.vue"),
      },
      {
        path: "/crafted/modals/wizards/create-app",
        name: "modals-wizards-create-app",
        component: () => import("@/views/crafted/modals/wizards/CreateApp.vue"),
      },
      {
        path: "/crafted/modals/wizards/create-account",
        name: "modals-wizards-create-account",
        component: () =>
          import("@/views/crafted/modals/wizards/CreateAccount.vue"),
      },
      {
        path: "/crafted/widgets/lists",
        name: "widgets-list",
        component: () => import("@/views/crafted/widgets/Lists.vue"),
      },
      {
        path: "/crafted/widgets/statistics",
        name: "widgets-statistics",
        component: () => import("@/views/crafted/widgets/Statistics.vue"),
      },
      {
        path: "/crafted/widgets/charts",
        name: "widgets-charts",
        component: () => import("@/views/crafted/widgets/Charts.vue"),
      },
      {
        path: "/crafted/widgets/mixed",
        name: "widgets-mixed",
        component: () => import("@/views/crafted/widgets/Mixed.vue"),
      },
      {
        path: "/crafted/widgets/tables",
        name: "widgets-tables",
        component: () => import("@/views/crafted/widgets/Tables.vue"),
      },
      {
        path: "/crafted/widgets/feeds",
        name: "widgets-feeds",
        component: () => import("@/views/crafted/widgets/Feeds.vue"),
      },
    ],
  },

  {
    path: "/",
    component: () => import("@/components/page-layouts/Auth.vue"),
    children: [
      {
        path: "/sign-in",
        name: "sign-in",
        meta: { auth: false },
        component: () =>
          import("@/views/crafted/authentication/basic-flow/SignIn.vue"),
      },
      {
        path: "/sign-up",
        name: "sign-up",
        meta: { auth: false },

        component: () =>
          import("@/views/crafted/authentication/basic-flow/SignUp.vue"),
      },
      {
        path: "/password-reset",
        name: "password-reset",
        meta: { auth: false },

        component: () =>
          import("@/views/crafted/authentication/basic-flow/PasswordReset.vue"),
      },
    ],
  },
  {
    path: "/multi-step-sign-up",
    name: "multi-step-sign-up",
    component: () =>
      import("@/views/crafted/authentication/MultiStepSignUp.vue"),
  },
  {
    // the 404 route, when none of the above matches
    path: "/404",
    name: "404",
    component: () => import("@/views/crafted/authentication/Error404.vue"),
  },
  {
    path: "/500",
    name: "500",
    component: () => import("@/views/crafted/authentication/Error500.vue"),
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/404",
  },
];

const router = createRouter({
  history: createWebHashHistory(),
  routes,
});

router.beforeEach(async (r) => {
  // reset config to initial state
  store.commit(Mutations.RESET_LAYOUT_CONFIG);

  const needAuth =
    typeof r.meta.auth !== "undefined" ? !(r.meta.auth === false) : true;

  if (needAuth) {
    //const user = store.getters.currentUser;
    if (AuthService.getToken().length === 0) {
      return { name: "sign-in" };
    }

    const requiredPermission = r.meta.permission;
    if (typeof requiredPermission === "string") {
      const authStore = useAuthStore();
      if (typeof authStore.currentUser.id === "undefined") {
        try {
          await authStore.me();
        } catch {
          return { name: "sign-in" };
        }
      }
      if (!authStore.currentUser.permissions?.includes(requiredPermission)) {
        return { name: "404" };
      }
    }

    /*
    if (typeof user.id === "undefined") {
      router.push({ name: "sign-in" }).then(() => {
        console.warn("not auth");
      });
    }*/
    /*
    await store
      .dispatch(Actions.VERIFY_AUTH, {
        api_token: JwtService.getToken(),
      })
      .then(() => {

        if (typeof store.getters.getErrors.loginPage !== "undefined") {
          router.push({ name: "sign-in" });
        }
      });*/
  }

  // Scroll page to top on every route change
  setTimeout(() => {
    window.scrollTo(0, 0);
  }, 100);
});

export default router;
